<template>
  <div class="user-menu" @keydown.escape="closeMenu">
    <RoundButton
      v-on-click-outside="closeMenu"
      icon="fa-solid fa-user"
      aria-label="Ouvrir le menu d'utilisateur"
      @click="toggleMenu()"
    />

    <div v-if="isMenuOpen" class="user-menu-items" @keydown="handleTabExit">
      <RouterLink
        v-focus
        class="user-menu__item"
        to="/user-settings/account"
        @click="toggleMenu"
      >
        <i class="fa-solid fa-user"></i>
        Mon compte
      </RouterLink>
      <BaseLink
        class="user-menu__item"
        href="https://follaw.crisp.help/fr/"
        @click="toggleMenu"
      >
        <i class="fa-solid fa-question"></i>
        Centre d'aide
      </BaseLink>
      <button
        v-focus
        class="user-menu__item user-menu__item--logout"
        @click="logout()"
        @keydown.enter.prevent="logout()"
      >
        <i class="fa-regular fa-arrow-right-from-bracket"></i>
        Me déconnecter
      </button>
    </div>

    <PopupDialog ref="confirmationDialog" title="Déconnexion">
      <template #body>
        <p>Êtes-vous sûr de vouloir vous déconnecter ?</p>
      </template>
      <template #footer>
        <button v-focus class="btn-delete" @click="confirmLogout()">
          Me déconnecter
        </button>
      </template>
    </PopupDialog>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import PopupDialog from "@follaw-fr/components/reusable/atoms/PopupDialog.vue";
import { useAuthStore } from "@follaw-fr/stores/auth";
import BaseLink from "../atoms/BaseLink.vue";
import RoundButton from "@follaw-fr/components/reusable/atoms/RoundButton.vue";
import { useToggleMenu } from "@follaw-fr/composables/useToggleMenu";

const authStore = useAuthStore();
const confirmationDialog = ref(null);

const { isMenuOpen, toggleMenu, closeMenu } = useToggleMenu();

function logout() {
  closeMenu();
  confirmationDialog.value?.open();
}

function confirmLogout() {
  authStore.logoutRequest();
  confirmationDialog.value?.close();
}

const handleTabExit = (event) => {
  if (event.key === "Escape") {
    closeMenu();
    return;
  }

  const focusableElements = event.currentTarget.querySelectorAll("button, a");
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (
    document.activeElement === firstElement &&
    event.shiftKey &&
    event.key === "Tab"
  ) {
    closeMenu();
  }

  if (document.activeElement === lastElement && !event.shiftKey) {
    closeMenu();
  }
};
</script>

<style lang="scss" scoped>
.user-menu {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;

  .user-menu-items {
    position: absolute;
    top: 3em;
    right: -1.5em;
    width: 230px;
    display: flex;
    flex-direction: column;

    border-radius: 5px;
    padding: 8px;
    background-color: white;
    z-index: 1000;
    box-shadow: 0px 6px 40px 0px rgba(0, 0, 0, 0.16);

    &:before {
      content: "";
      position: absolute;
      border: 0.75em solid;
      border-color: transparent transparent #fff transparent;
      right: 1.7em;
      top: -1.5rem;
    }
  }
}

.user-menu__item {
  position: relative;
  text-align: left;
  display: flex;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  color: var(--clr-n8);

  &:hover {
    background-color: var(--clr-p1);

    &:not(.user-menu__item--logout) {
      color: var(--clr-n8);
    }
  }

  &--logout {
    color: var(--clr-e300);
  }
}
</style>
